import React, { useEffect, useState } from 'react'
import { t } from 'ttag'
import { CustomType, GoalId, GoalWithProgress, ProgressType, UserId, UserCompanyDto } from '../../services/api.types'
import { Box, InputBase, NativeSelect } from '@material-ui/core'
import YumButton from '../YumButton/YumButton'
import dayjs from '../../dayjsConfig'
import YumCustomEntryConfirmModal from '../YumCustomEntryConfirmModal/YumCustomEntryConfirmModal'
import {
  CustomTypeCategory,
  getCustomTypeText,
  getCustomTypeTextById,
  getCustomTypeWithLegacy,
  getCustomUnitText,
  getCustomUnitTextById,
  getPointsTypeText,
} from '../../services/CustomTypes'
import { formatThousands } from '../../common/helpers/translations'
import {
  challengeGoalGray,
  challengeGoalGreen,
  darkBlue,
  darkGreen,
  lightBlue,
  lighterGreen,
  successGreen,
} from '../../styles/material-ui-theme-v2'
import { PluginListenerHandle } from '@capacitor/core'
import { App } from '@capacitor/app'
import { capitalizeFirstLetter, convertUTCToLocalDate } from '../../common/helpers/date'
import { currentLocale, isRtl } from '../../i18nInit'
import {
  getChallengeGoalUnits,
  getChallengeProgressUnits,
  isActivityIndexType,
  isAutomatedType,
  isBonusType,
  isCollectiveType,
  isDistanceType,
  isSimpleDayType,
} from '../../common/helpers/challenges'
import { ReactComponent as StepsIcon } from '../../assets/v3/icons/ranking/steps.svg'
import { ReactComponent as CaloriesIcon } from '../../assets/v3/icons/ranking/calories.svg'
import { ReactComponent as MinutesIcon } from '../../assets/v3/icons/ranking/minutes.svg'
import { CustomProgressInnerState, DailyProgressState } from '../../views/Progress/store/ProgressReducer'
import { PointsLogic, PointsLogicId } from '../../views/ChallengeAdmin/components/ChallengePoints/ChallengePoints'
import { YumActivityIcon } from '../YumActivityIcon/YumActivityIcon'
import makeStyles from '@material-ui/core/styles/makeStyles'
import { getCustomProgressForPoints } from '../../views/Progress/store/ProgressActions'
import { GetCustomProgressForPointsState } from '../../views/Progress/store/ProgressSagas'
import { store } from '../../index'
import { clearNotification } from '../../common/notifications/NotificationActions'
import { dailyMaxLabel } from '../../views/ChallengeInfo/ChallengeInfo'
import { hasFeatureFlag } from '../../common/helpers/featureFlags'

// Dynamic max amount based on feature flag and activity type
const getMaxCustomEntryAmount = (
  userCompanies: UserCompanyDto[],
  customType: CustomType,
  dailyMax?: number,
): number => {
  // Check if daily max limit feature flag is enabled
  const isDailyMaxLimitEnabled = hasFeatureFlag(userCompanies, 'limitManualEntryToDailyMax')

  // If daily max limit is enabled and dailyMax is provided, use it
  if (isDailyMaxLimitEnabled && dailyMax && dailyMax > 0) {
    return dailyMax
  }

  // Check if time-based limit feature flag is enabled
  const isTimeLimitEnabled = hasFeatureFlag(userCompanies, 'limitManualEntryTime')

  // Check if activity type is time-based (ends with "_MIN", "_MINUTES", "_HR", or "_SEC")
  const isTimeBasedActivity =
    customType.endsWith('_MIN') ||
    customType.endsWith('_MINUTES') ||
    customType.endsWith('_HR') ||
    customType.endsWith('_SEC')

  // Return appropriate limit based on time unit if both conditions are met, otherwise default
  if (isTimeLimitEnabled && isTimeBasedActivity) {
    if (customType.endsWith('_MIN') || customType.endsWith('_MINUTES')) {
      return 1440 // 24 hours in minutes
    } else if (customType.endsWith('_HR')) {
      return 24 // 24 hours
    } else if (customType.endsWith('_SEC')) {
      return 86400 // 24 hours in seconds
    }
  }

  return 1000000 // Default limit
}

export const getRoundPercentage = (num: number) => Math.round((num + Number.EPSILON) * 100)
export const getFloorPercentage = (num: number) => Math.floor((num + Number.EPSILON) * 100)

export interface IStateProps {
  goal: GoalWithProgress
  isMeView: boolean
  isBonus?: boolean
  userId?: UserId
  //dailyDiary: UserDailyDiaryProgress[]
  dailyDiary: DailyProgressState
  //customProgress: UserCustomDailyProgress[]
  customProgress: CustomProgressInnerState
  onSubmit(): void
  manualEntryProgressType: CustomType
  manualEntryPointsLogicId: PointsLogicId | null
  isManualEntryInProgress: boolean
  userCompanies: UserCompanyDto[]
}

export interface CustomEntryState {
  amount: number
  customType: CustomType
  date: string
  goalId: GoalId

  endDate: string
  startDate: string
  participating: boolean

  isCollective?: boolean
  isActivityIndex?: boolean
  manualEntryEnabled?: boolean

  pointsLogicId: PointsLogicId | null
  isPoints?: boolean

  isMeView: boolean

  isBonus?: boolean
  userId?: UserId
}

export interface IDispatchProps {
  addCustomEntry(payload: CustomEntryState): void
  setManualEntryProgressType(payload: CustomType): void
  setManualEntryPointsLogicId(payload: PointsLogicId | null): void
  getCustomProgressForPoints(payload: GetCustomProgressForPointsState): void
  //onSubmit(): void
}

const useStyles = makeStyles((theme) => ({
  wrapper: {
    outline: 'none',
  },
  button: {
    display: 'inline-block',
    padding: '0 8px',
    lineHeight: '32px',
    fontSize: 15,
    fontWeight: 'bold',
    borderRadius: 8,
    width: 'auto',
    minWidth: 'auto',
    margin: 0,
    textTransform: 'none',
    whiteSpace: 'nowrap',
  },
  buttonGreen: {
    backgroundColor: darkGreen, //successGreen,
    '&:hover': {
      backgroundColor: darkGreen, //successGreen,
    },
  },
  fullWidth: {
    width: '100%',
  },
  smallButton: {
    display: 'inline-block',
    padding: '0 8px',
    lineHeight: '16px',
    fontSize: 12,
    fontWeight: 'bold',
    borderRadius: 6,
    width: 'auto',
    minWidth: 'auto',
    margin: 0,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    height: '20px',
  },
  plainButton: {
    display: 'inline-flex',
    lineHeight: '32px',
    fontSize: 15,
    fontWeight: 'bold',
    minWidth: 'auto',
    margin: 0,
    textTransform: 'none',
    backgroundColor: 'white',
    height: 32,
    width: 32,
    borderRadius: 8,
    padding: 0,
    userSelect: 'none',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      '@media (hover: none)': {
        backgroundColor: '#ffffff',
      },
    },
  },
  plainInput: {
    flexGrow: 1,
    background: 'none',
    border: 'none',
    '& input': {
      backgroundColor: 'rgba(0,0,0,.05)',
    },
    '& input:focus': {
      backgroundColor: 'rgba(0,0,0,.1)',
    },
    marginRight: isRtl ? 0 : '10px',
    marginLeft: isRtl ? '10px' : 0,
  },
  plainInputInner: {
    padding: 0,
    height: '100%',
    textAlign: 'center',
    fontSize: 21,
    fontWeight: 'bold',
    borderRadius: '8px',
  },
  plainSelect: {
    border: 'none',
    textAlign: 'center',
    '&:before, &:after': {
      content: 'none !important',
    },
  },
  whiteSelectCaret: {
    '& .MuiNativeSelect-icon': {
      color: 'white',
    },
  },

  amountTuneButton: {
    backgroundColor: 'rgba(0, 111, 255, 0.1)',
    display: 'inline-flex',
    height: 32,
    '&:hover, &:focus': {
      backgroundColor: 'rgba(0, 111, 255, 0.15)',
    },
    marginRight: isRtl ? 0 : '10px',
    marginLeft: isRtl ? '10px' : 0,
  },
  doneCheckmark: {
    marginLeft: '7px',
  },

  mainWrapper: {
    backgroundColor: lightBlue,
    padding: '10px',
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
    transition: 'all 0.2s ease-out',
  },
  mainHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    //gap: 10,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    marginBottom: '10px',
    userSelect: 'none',
  },
  activeDateWrapper: { textAlign: 'center', marginLeft: '10px', marginRight: '10px' },
  activeDate: {
    fontWeight: 500,
    fontSize: 13,
    lineHeight: '16px',
    opacity: 0.8,
  },
  activeDateSelectInput: {
    fontSize: '14px',
    textAlign: 'center',
    paddingLeft: 5,
    lineHeight: '16px',
    height: '16px',
    padding: '8px 22px 8px 5px',
  },

  activityName: {
    fontWeight: 500,
    fontSize: 15,
    lineHeight: '18px',
    textAlign: 'center',
  },
  dayValue: {
    fontWeight: 500,
    fontSize: 28,
    lineHeight: '33px',
  },
  dayGoal: {
    fontWeight: 500,
    fontSize: 28,
    lineHeight: '33px',
    opacity: 0.8,
  },
  units: {
    fontWeight: 500,
    fontSize: 18,
    lineHeight: '21px',
    opacity: 0.8,
  },

  progressBar: {
    borderRadius: 10,
    height: 8,
    marginTop: 8,
    background: 'rgba(255, 255, 255, 0.5)',
  },
  progressBarInner: {
    backgroundColor: darkBlue,
    borderRadius: 10,
    height: 8,
    marginTop: 8,
    transition: 'all 0.2s ease-out',
  },

  formWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    //gap: 10,
    backgroundColor: 'white', //'#f8f8f8',
    padding: 10,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    height: '52px',
    //marginBottom: 10,
  },
}))

export function getWidthOfText(txt: string, fontWeight?: string, fontSize?: string) {
  const s = document.createElement('span')
  s.textContent = txt
  s.style.fontFamily =
    '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", "Arial", "sans-serif", "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"'
  s.style.fontWeight = fontWeight || '500'
  s.style.fontSize = fontSize || '14px'
  s.style.textTransform = fontWeight && fontWeight !== '600' ? 'uppercase' : 'none'

  document.body.appendChild(s)
  const w = s.offsetWidth
  document.body.removeChild(s)

  return w
}

const getDailyMax = (
  firstPointsLogic: PointsLogic | null | undefined,
  customType: CustomType | null,
  hasSimpleManualEntry: boolean,
  dailyGoal: number | null,
  units: string,
  withLabel?: boolean,
) => {
  let showDescription = false
  if (firstPointsLogic) {
    showDescription =
      firstPointsLogic.category !== CustomTypeCategory.CUSTOM_SIMPLE &&
      firstPointsLogic.category !== CustomTypeCategory.BONUS_SIMPLE &&
      !!firstPointsLogic.dailyGoal
  } else {
    showDescription = hasSimpleManualEntry && customType !== CustomType.CUSTOM
  }
  if (!showDescription) return ''

  const amount = formatThousands(dailyGoal || 1)
  // TODO: Can "defaultPointsCustomUnit || customUnit" be replaced with just "units"?
  //const units = getCustomUnitText(customType, customType, defaultPointsCustomUnit || customUnit)
  return `${dailyMaxLabel}: ${amount} ${units}`
  //return withLabel ? `${dailyMaxLabel}: ${amount} ${units}` : ` (${amount} ${units})`
}

export default function YumManualEntryForm(props: IStateProps & IDispatchProps) {
  const {
    goal,
    dailyDiary: dailyDiaryRaw,
    customProgress: customProgressRaw,
    addCustomEntry,
    onSubmit,
    setManualEntryProgressType,
    setManualEntryPointsLogicId,
    getCustomProgressForPoints,
    manualEntryProgressType,
    manualEntryPointsLogicId,
    isMeView,
    isManualEntryInProgress,
    isBonus,
    userId,
    userCompanies,
  } = props
  const { id: goalId, customName, customUnit, progressType, participating } = goal

  const isPoints = goal.customType === CustomType.POINTS
  let pointsLogic = (isPoints ? JSON.parse(goal.pointsLogic) : []) as PointsLogic[]

  const challengeStartDate = dayjs(convertUTCToLocalDate(new Date(goal.startDate)))
  const challengeEndDate = dayjs(convertUTCToLocalDate(new Date(goal.endDate)))

  useEffect(() => {
    // If isBonus, load user previous data
    if (isBonus) {
      getCustomProgressForPoints({
        goalId,
        startDate: challengeStartDate.locale('en').format('YYYY-MM-DD'),
        endDate: challengeEndDate.locale('en').format('YYYY-MM-DD'),
        pointsLogic,
        userId,
      })
    }
  }, [])

  // Show bonus points type only to admins
  if (isPoints) {
    // Filter out any points logic items with categories not defined in CustomTypeCategory
    pointsLogic = pointsLogic.filter((a) => Object.values(CustomTypeCategory).includes(a.category))

    pointsLogic = isBonus
      ? pointsLogic.filter((a) => isBonusType(a.category))
      : pointsLogic.filter((a) => !isBonusType(a.category))
  }

  // If manual entry is disabled, remove automatic/non-custom activities.
  if (isPoints && !goal.manualEntryEnabled) {
    pointsLogic = pointsLogic.filter((a) => a.unit === CustomType.CUSTOM || !isAutomatedType(a.unit))
  }

  if (pointsLogic === null) pointsLogic = [] as PointsLogic[]
  const hasPointsLogic = pointsLogic && pointsLogic.length

  // TODO: If points, this should be the first one.
  let defaultPointsLogicId = hasPointsLogic ? manualEntryPointsLogicId || pointsLogic[0].id : null
  if (hasPointsLogic && !pointsLogic.some((p: PointsLogic) => p.id === defaultPointsLogicId)) {
    defaultPointsLogicId = pointsLogic[0].id
  }

  const [pointsLogicId, setPointsLogicId] = useState(defaultPointsLogicId as string | null)
  const isActivityIndex = isActivityIndexType(progressType)

  const isPersistenceCustom = progressType === ProgressType.PERSISTENCE_CUSTOM

  const firstPointsLogic = hasPointsLogic ? pointsLogic.find((p: PointsLogic) => p.id === pointsLogicId) : null
  const defaultPointsType = firstPointsLogic?.unit || null
  const defaultPointsCategory = firstPointsLogic?.category || null
  const defaultPointsCustomUnit = firstPointsLogic?.customUnitName || null
  const defaultPointsCustomActivityName = firstPointsLogic?.customActivityName || null
  const defaultPointsSimpleDayType = isSimpleDayType(firstPointsLogic?.category as CustomTypeCategory)
  const defaultPointsSimpleManualEntry = firstPointsLogic?.simpleManualEntry || false
  let defaultPointsDailyGoal = firstPointsLogic?.dailyGoal || 1

  const isSimple =
    CustomTypeCategory.CUSTOM_SIMPLE === defaultPointsCategory ||
    CustomTypeCategory.BONUS_SIMPLE === defaultPointsCategory

  if (isSimple) {
    defaultPointsDailyGoal = 1
  }

  const hasSimpleManualEntry = goal.simpleManualEntry && progressType === ProgressType.PERSISTENCE_CUSTOM && !isPoints

  // TODO: Simplify this.
  const [isBinary, setIsBinary] = useState(
    progressType === ProgressType.PERSISTENCE_CUSTOM_BINARY ||
      (defaultPointsCategory && isSimple) ||
      isSimpleDayType(goal.customType as unknown as CustomTypeCategory) ||
      defaultPointsSimpleDayType ||
      hasSimpleManualEntry ||
      defaultPointsSimpleManualEntry,
  )

  // TODO: What if it doesn't match this GOAL ???
  const defaultProgressType = (
    isActivityIndex
      ? manualEntryProgressType &&
        [CustomType.STEPS, CustomType.CALORIES, CustomType.INTENSIVE_MINUTES].includes(manualEntryProgressType)
        ? manualEntryProgressType
        : CustomType.STEPS
      : progressType
  ) as ProgressType

  let initCustomType = goal.customType
  if (initCustomType === CustomType.POINTS && defaultPointsType) initCustomType = defaultPointsType
  // console.log('initCustomType', initCustomType, 'defaultProgressType', defaultProgressType)
  const [customType, setCustomType] = useState(getCustomTypeWithLegacy(initCustomType, defaultProgressType))
  const isCustomCustom = customType === CustomType.CUSTOM

  const defaultCustomActivityName = defaultPointsCustomActivityName || goal.customName

  const [dailyPersistenceGoal, setDailyPersistenceGoal] = useState(
    isPoints ? defaultPointsDailyGoal : goal.dailyPersistenceGoal,
  )

  const initDailyDiary = customType && dailyDiaryRaw && dailyDiaryRaw[customType] ? dailyDiaryRaw[customType] : []
  const [dailyDiary, setDailyDiary] = useState(initDailyDiary)

  const pointsId = pointsLogicId || 'default'
  const initCustomProgress =
    goalId && customProgressRaw && customProgressRaw[pointsId] ? customProgressRaw[pointsId] : []
  const [customProgress, setCustomProgress] = useState(initCustomProgress)

  const [units, setUnits] = useState(
    isActivityIndex || isPoints
      ? getCustomUnitText(customType, customType, defaultPointsCustomUnit || customUnit)
      : getChallengeGoalUnits(goal),
  )

  const defaultActivityName = defaultPointsCategory
    ? [
        CustomTypeCategory.CUSTOM_SIMPLE,
        CustomTypeCategory.CUSTOM_WITH_AMOUNT,
        CustomTypeCategory.BONUS_SIMPLE,
        CustomTypeCategory.BONUS_WITH_AMOUNT,
      ].includes(defaultPointsCategory)
      ? defaultPointsCustomActivityName
      : getCustomTypeTextById(defaultPointsCategory)
    : getCustomTypeText(customType, customName)

  const amountDescription = getDailyMax(firstPointsLogic, customType, hasSimpleManualEntry, dailyPersistenceGoal, units)
  const [activityName, setActivityName] = useState(defaultActivityName) // + amountDescription)
  const [dailyMaxText, setDailyMaxText] = useState(amountDescription)

  const classes = useStyles()
  const [amount, setAmount] = useState(1)
  const [total, setTotal] = useState(amount)

  const MAX_MANUAL_ENTRY_DAYS = goal.maxManualEntryDays || 7

  let startDate = isBonus
    ? challengeStartDate
    : dayjs()
        .subtract(MAX_MANUAL_ENTRY_DAYS - 1, 'day')
        .startOf('day')
  let endDate = dayjs().startOf('day')

  if (challengeStartDate.isAfter(startDate)) startDate = dayjs(challengeStartDate)
  if (challengeEndDate.isBefore(endDate)) endDate = dayjs(challengeEndDate)

  const challengeHasStarted = challengeStartDate.isBefore(new Date())
  const challengeHasEnded = challengeEndDate.isBefore(new Date())

  const gracePeriodEnd = challengeEndDate.add(3, 'day')
  const gracePeriodEnded = dayjs().isAfter(gracePeriodEnd)

  const isActive = challengeHasStarted && !gracePeriodEnded
  const placeholderDay = challengeHasEnded ? dayjs(challengeEndDate) : dayjs(challengeStartDate)
  const placeholderDayValue = placeholderDay.locale('en').format('YYYY-MM-DD')

  const getDates = (startDate: dayjs.Dayjs, endDate: dayjs.Dayjs) => {
    let now = dayjs(startDate)
    const dates = []
    const fmt = 'LL'
    while (now.isSameOrBefore(endDate)) {
      dates.push({
        text: capitalizeFirstLetter(
          now.calendar(null, {
            sameDay: `[${t`Today`}], ${fmt}`,
            lastDay: `dddd, ${fmt}`, //`[${t`Yesterday`}], ${fmt}`,
            nextDay: `[${t`Tomorrow`}], ${fmt}`,
            lastWeek: `dddd, ${fmt}`,
            nextWeek: `dddd, ${fmt}`,
            sameElse: `dddd, ${fmt}`,
          }),
        ),
        value: now.locale('en').format('YYYY-MM-DD'),
      })
      now = now.add(1, 'day')
    }
    if (!isActive && !dates.length) {
      dates.push({
        text: capitalizeFirstLetter(
          placeholderDay.locale(currentLocale).calendar(null, {
            sameDay: `[${t`Today`}], ${fmt}`,
            lastDay: `dddd, ${fmt}`, //`[${t`Yesterday`}], ${fmt}`,
            nextDay: `[${t`Tomorrow`}], ${fmt}`,
            lastWeek: `dddd, ${fmt}`,
            nextWeek: `dddd, ${fmt}`,
            sameElse: `dddd, ${fmt}`,
          }),
        ),
        value: placeholderDayValue,
      })
    }
    return dates
  }

  const defaultActiveDate = !isActive ? placeholderDayValue : endDate.locale('en').format('YYYY-MM-DD')
  const [activeDate, setActiveDate] = useState(defaultActiveDate)

  const [activeDateText, setActiveDateText] = useState('')
  const [activeDateWidth, setActiveDateWidth] = useState(225)

  const [prevDate, setPrevDate] = useState(endDate.locale('en').subtract(1, 'day') as dayjs.Dayjs | null)
  const [nextDate, setNextDate] = useState(null as dayjs.Dayjs | null)

  const [dayValue, setDayValue] = useState(0)
  const [manualDayValue, setManualDayValue] = useState(0)
  const [dayPercentage, setDayPercentage] = useState(0)

  const [activeValue, setActiveValue] = useState(
    defaultPointsSimpleManualEntry &&
      defaultPointsCategory &&
      ![
        CustomTypeCategory.CUSTOM_SIMPLE,
        CustomTypeCategory.CUSTOM_WITH_AMOUNT,
        CustomTypeCategory.BONUS_SIMPLE,
        CustomTypeCategory.BONUS_WITH_AMOUNT,
      ].includes(defaultPointsCategory) &&
      defaultPointsDailyGoal
      ? defaultPointsDailyGoal
      : hasSimpleManualEntry && dailyPersistenceGoal
        ? dailyPersistenceGoal
        : 1,
  )

  const calculateTotal = () => {
    let total = dayValue + (amount || 0)
    if (total < 0) total = 0
    setTotal(total)
  }

  const getDailyGoal = () => {
    //if (isCustomCustom) return 1
    // if (isBinary) return 1
    if (isActivityIndex) {
      if (customType === CustomType.STEPS) return 12000
      // TODO: We should somehow get daily calories goal.
      else if (customType === CustomType.CALORIES) return 2500
      else if (customType === CustomType.INTENSIVE_MINUTES) return 20
      else return 1
    }
    return dailyPersistenceGoal || 1
  }

  const dailyGoal = getDailyGoal()

  const calculateDay = () => {
    let value = 0
    let manualValue = 0
    if (isCustomCustom) {
      value = customProgress?.find((r) => r.date === activeDate)?.value || 0
      manualValue = value
    } else {
      value = dailyDiary?.find((r) => r.date === activeDate)?.sum || 0
      manualValue = dailyDiary?.find((r) => r.date === activeDate)?.manualValue || 0
    }
    /*console.log(
      'calculateDay | isCustomCustom:',
      isCustomCustom,
      'manualValue:',
      manualValue,
      'customType:',
      customType,
      'value:',
      value,
      'dailyGoal:',
      dailyGoal,
    )*/
    const num = value / (dailyGoal || 0)
    const percentage = isDistance ? getFloorPercentage(num) : getRoundPercentage(num)
    setDayValue(value)
    setManualDayValue(manualValue)
    setDayPercentage(!dailyGoal ? 0 : percentage < 100 ? percentage : 100)
    setAmount(validateAmount(amount, value))
  }

  useEffect(() => {
    calculateTotal()
  }, [dayValue, amount])

  /*const syncProgress = () => {

  }*/

  useEffect(() => {
    //syncProgress()

    let appStateChangeListener: PluginListenerHandle | undefined
    let lastActiveDate = dayjs().locale('en').format('YYYY-MM-DD')
    const setAppStateChangeListener = async () => {
      appStateChangeListener = await App.addListener('appStateChange', async ({ isActive }) => {
        if (isActive) {
          //syncProgress()

          // Check if device date has changed.
          const currentDate = dayjs().locale('en').format('YYYY-MM-DD')
          if (lastActiveDate !== currentDate) {
            const newActiveDate = challengeEndDate.isBefore(new Date())
              ? challengeEndDate.format('YYYY-MM-DD')
              : currentDate
            setActiveDate(newActiveDate)
            setNextDate(null)
            lastActiveDate = currentDate
          }
        }
      })
    }
    setAppStateChangeListener()

    return () => {
      if (appStateChangeListener) {
        try {
          appStateChangeListener.remove()
        } catch (error) {
          console.error('YumManualEntryForm | appStateChangeListener.remove() error', error)
        }
      }
    }
  }, [])

  useEffect(() => {
    let prev: dayjs.Dayjs | null = dayjs(activeDate).subtract(1, 'day')
    let next: dayjs.Dayjs | null = dayjs(activeDate).add(1, 'day')

    // Allow going back max 7 (or custom number) days.
    if (!isBonus && !dayjs().subtract(MAX_MANUAL_ENTRY_DAYS, 'day').isBefore(prev)) {
      prev = null
    }
    // Allow going forward until today.
    if (!dayjs().isSameOrAfter(next)) {
      next = null
    }
    // Allow going back to goal start, not further.
    if (challengeStartDate.isAfter(prev)) {
      prev = null
    }
    // Don't allow going forward from end of challenge (past goals).
    if (challengeEndDate.isBefore(next)) {
      next = null
    }
    setPrevDate(prev)
    setNextDate(next)
    calculateDay()

    const text = getDates(startDate, endDate).find((d) => d.value === activeDate)?.text || ''
    setActiveDateText(text)
    setActiveDateWidth(getWidthOfText(text))
  }, [activeDate])

  useEffect(() => {
    const newDailyDiary = customType && dailyDiaryRaw && dailyDiaryRaw[customType] ? dailyDiaryRaw[customType] : []
    setDailyDiary(newDailyDiary)
  }, [dailyDiaryRaw, customType])

  // TODO: We need something similar for customDiary
  useEffect(() => {
    const pointsId = pointsLogicId || 'default'
    const newCustomProgress =
      goalId && customProgressRaw && customProgressRaw[pointsId] ? customProgressRaw[pointsId] : []
    setCustomProgress(newCustomProgress)
  }, [customProgressRaw, pointsLogicId])

  useEffect(() => {
    // console.log('useEffect: dailyDiary, customProgress')
    calculateDay()
  }, [dailyDiary, customProgress])

  // Clear notifications whenever the active date changes
  useEffect(() => {
    store.dispatch(clearNotification())
  }, [activeDate])

  const handlePrevDay = () => {
    if (prevDate) {
      setActiveDate(prevDate.locale('en').format('YYYY-MM-DD'))
    }
  }

  const handleNextDay = () => {
    if (nextDate) {
      setActiveDate(nextDate.locale('en').format('YYYY-MM-DD'))
    }
  }

  const validateAmount = (value: any, dayVal?: number) => {
    if (!value) value = 0
    let amount = isDistance ? parseFloat(parseFloat(value).toFixed(2)) : parseInt(value)
    if (isNaN(value)) return 0
    if (dayVal === undefined) dayVal = dayValue

    // Max validation - use dynamic max amount based on feature flag and activity type
    // For POINTS challenges, use defaultPointsDailyGoal; otherwise use dailyPersistenceGoal
    const dailyMaxValue = isPoints ? defaultPointsDailyGoal : goal.dailyMax
    const maxCustomEntryAmount = getMaxCustomEntryAmount(
      userCompanies,
      customType || CustomType.CUSTOM,
      dailyMaxValue || undefined,
    )
    const max = maxCustomEntryAmount - dayVal
    if (amount > max) amount = max

    // Min validation.
    if (manualDayValue === 0) {
      if (amount < 0) amount = 0
    } else {
      if (manualDayValue < 0 - amount) amount = 0 - manualDayValue
    }
    /*if (dayVal === 0) {
      if (amount < 0) amount = 0
    } else {
      if (dayVal < 0 - amount) amount = 0 - dayVal
    }*/

    return amount
  }

  const decrementAmount = () => {
    setAmount(validateAmount(amount - 1))
  }

  const incrementAmount = () => {
    setAmount(validateAmount(amount + 1))
  }

  const isDistance = isDistanceType(customType)
  const decimalSeparator = '.'

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value.replace(',', decimalSeparator)
    if (!isDistance) {
      value = value.replace(decimalSeparator, '')
    } else {
      if (value === decimalSeparator) {
        value = '0' + decimalSeparator
      }
    }
    if (
      value === '-' ||
      value === '' ||
      (isDistance &&
        value != decimalSeparator &&
        (value.endsWith(decimalSeparator) || value.endsWith(decimalSeparator + '0')) &&
        value.split(decimalSeparator).length <= 2)
    ) {
      // @ts-ignore
      return setAmount(value)
    }
    setAmount(validateAmount(event.target.value))
  }

  const handleActiveDateChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setActiveDate(event.target.value as string)
  }

  const submitAdd = () => {
    const { progressType, manualEntryEnabled } = props.goal
    const type = customType as CustomType
    let amount = total

    if (isBinary) {
      if (dayValue >= activeValue) {
        amount = 0
      } else {
        amount = activeValue
      }
    }

    const isCollective = isCollectiveType(progressType)
    addCustomEntry({
      amount,
      customType: type,
      date: activeDate,
      goalId,

      startDate: challengeStartDate.locale('en').format('YYYY-MM-DD'),
      endDate: challengeEndDate.locale('en').format('YYYY-MM-DD'),
      participating,

      isCollective,
      isActivityIndex,
      manualEntryEnabled,

      pointsLogicId: isCustomCustom ? pointsLogicId : null,
      isPoints,

      isMeView: isMeView || !goal.splitRankingView,

      isBonus,
      userId,
    })
    onSubmit()
  }

  const digits = isDistance ? 2 : 0

  const amountForm = (
    <>
      <YumButton onClick={decrementAmount} className={[classes.button, classes.amountTuneButton].join(' ')}>
        <svg width="16" height="2" viewBox="0 0 16 2" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0 1L16 0.999999" stroke="#006FFF" strokeWidth="2" />
        </svg>
      </YumButton>
      <InputBase
        onChange={handleAmountChange}
        value={amount}
        className={classes.plainInput}
        inputProps={{
          className: classes.plainInputInner,
          inputMode: isDistance ? 'decimal' : 'numeric',
        }}
      />
      <YumButton onClick={incrementAmount} className={[classes.button, classes.amountTuneButton].join(' ')}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M8 0V16" stroke="#006FFF" strokeWidth="2" />
          <path d="M0 8L16 8" stroke="#006FFF" strokeWidth="2" />
        </svg>
      </YumButton>
      <YumCustomEntryConfirmModal
        date={activeDateText}
        amount={amount}
        total={total}
        onSubmit={submitAdd}
        className={classes.button}
        units={units}
        activityName={activityName || ''}
        disabled={!isActive}
        digits={digits}
        isManualEntryInProgress={isManualEntryInProgress}
      >
        {amount >= 0 ? t`Add` : t`Remove`}
      </YumCustomEntryConfirmModal>
    </>
  )

  // const dayGoal = hasSimpleManualEntry && goal.dailyPersistenceGoal ? goal.dailyPersistenceGoal : 1

  const binaryForm = (
    <>
      {/* TODO: Wrong - this should be daily value */}
      {dayValue >= dailyGoal ? (
        <YumCustomEntryConfirmModal
          date={activeDateText}
          amount={amount}
          total={0}
          onSubmit={submitAdd}
          className={[classes.button, classes.fullWidth, classes.buttonGreen].join(' ')}
          units={units}
          activityName={activityName || ''}
          binary={true}
          disabled={!isActive}
          digits={digits}
          isManualEntryInProgress={isManualEntryInProgress}
        >
          {t`Marked as done`}
          <svg
            className={classes.doneCheckmark}
            width="16"
            height="11"
            viewBox="0 0 16 11"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M1.5 4L6.5 9L14.5 1" stroke="white" strokeWidth="2" />
          </svg>
        </YumCustomEntryConfirmModal>
      ) : (
        <YumCustomEntryConfirmModal
          date={activeDateText}
          amount={amount}
          total={1}
          onSubmit={submitAdd}
          className={[classes.button, classes.fullWidth].join(' ')}
          units={units}
          activityName={activityName || ''}
          binary={true}
          disabled={!isActive}
          digits={digits}
          isManualEntryInProgress={isManualEntryInProgress}
        >
          {t`Mark as done`}
        </YumCustomEntryConfirmModal>
      )}
    </>
  )

  /*const handleCustomTypeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const id = event.target.value as PointsLogicId
    //const activity = event.target.value as PointsLogic
    //const type = activity.unit

    const activity = pointsLogic.find((a: PointsLogic) => a.id === id)
    if (activity) {
      const type = activity.unit

      setCustomType(type)
      setManualEntryProgressType(type)
      setManualEntryPointsLogicId(id)
      setIsBinary([CustomType.CUSTOM, CustomType.CUSTOM_SIMPLE].includes(type))
      setPointsLogicId(id)
    }
  }*/

  const setPointsCustomType = (id: PointsLogicId) => () => {
    // Clear notifications
    store.dispatch(clearNotification())

    const activity = pointsLogic.find((a: PointsLogic) => a.id === id)
    if (activity) {
      const type = activity.unit
      setCustomType(type)

      const units = getCustomUnitText(type, type, activity.customUnitName)

      const dailyGoalIsRelevant =
        activity.category !== CustomTypeCategory.CUSTOM_SIMPLE &&
        activity.category !== CustomTypeCategory.BONUS_SIMPLE &&
        activity.dailyGoal

      const amountDescription = dailyGoalIsRelevant
        ? `${dailyMaxLabel}: ${formatThousands(activity.dailyGoal || 1)} ${units}`
        : ''
      setActivityName(getPointsTypeText(activity)) // + amountDescription)
      setDailyMaxText(amountDescription)

      const value = dailyGoalIsRelevant && activity.simpleManualEntry && activity.dailyGoal ? activity.dailyGoal : 1
      setActiveValue(value)
      setDailyPersistenceGoal(value)

      setManualEntryProgressType(type)
      setManualEntryPointsLogicId(id)
      //setIsBinary([CustomType.CUSTOM, CustomType.CUSTOM_SIMPLE].includes(type))
      setIsBinary(
        activity.category === CustomTypeCategory.CUSTOM_SIMPLE ||
          activity.category === CustomTypeCategory.BONUS_SIMPLE ||
          (!!activity.simpleManualEntry && !!activity.dailyGoal) ||
          isSimpleDayType(activity.category),
      )
      setPointsLogicId(id)
      setUnits(units)
    }
  }

  const selectSteps = () => {
    const type = CustomType.STEPS
    setCustomType(type)
    setManualEntryProgressType(type)
    setUnits(getCustomUnitText(type))
  }

  const selectCalories = () => {
    const type = CustomType.CALORIES
    setCustomType(type)
    setManualEntryProgressType(type)
    setUnits(getCustomUnitText(type))
  }

  const selectMinutes = () => {
    const type = CustomType.INTENSIVE_MINUTES
    setCustomType(type)
    setManualEntryProgressType(type)
    setUnits(getCustomUnitText(type))
  }

  const activityIndexTypeSelect = (
    <>
      {/*<NativeSelect className={classes.plainSelect} value={customType} onChange={handleCustomTypeChange}>
        <option value={CustomType.STEPS}>{t`Steps`}</option>
        <option value={CustomType.CALORIES}>{unitsDisplayDurationCalorieLong}</option>
        <option value={CustomType.INTENSIVE_MINUTES}>{t`Intensive minutes`}</option>
      </NativeSelect>*/}
      <div>
        <StepsIcon
          style={{ margin: '0 5px', cursor: 'pointer', opacity: customType === CustomType.STEPS ? 1 : 0.5 }}
          onClick={selectSteps}
        />
        <CaloriesIcon
          style={{ margin: '0 5px', cursor: 'pointer', opacity: customType === CustomType.CALORIES ? 1 : 0.5 }}
          onClick={selectCalories}
        />
        <MinutesIcon
          style={{ margin: '0 5px', cursor: 'pointer', opacity: customType === CustomType.INTENSIVE_MINUTES ? 1 : 0.5 }}
          onClick={selectMinutes}
        />
      </div>
    </>
  )

  const pointsLogicIdSelect = (
    <>
      {/*<NativeSelect className={classes.plainSelect} value={pointsLogicId} onChange={handleCustomTypeChange}>
        {pointsLogic.map((activity: PointsLogic, index: number) => (
          <option key={index} value={activity.id}>
            {getCustomTypeText(activity.unit, activity.customActivityName) || getCustomTypeTextById(activity.unit)}
          </option>
        ))}
      </NativeSelect>*/}
      <div>{activityName}</div>
      <div style={{ opacity: 0.75, fontSize: 12 }}>{dailyMaxText}</div>

      <div>
        {pointsLogic.length > 1 &&
          pointsLogic.map((activity: PointsLogic, index: number) => {
            // TODO: Set icon green, if done.
            /*const isCustom = activity.unit === CustomType.CUSTOM
            const goalNum = isCustom ? 1 : activity.dailyGoal
            const sum =
              (isCustom
                ? customProgress?.find((r) => r.date === activeDate)?.value
                : dailyDiary?.find((r) => r.date === activeDate)?.sum) || 0
            const num = goalNum ? sum / (goalNum || 0) : 0
            const percentage = isDistanceType(activity.unit) ? getFloorPercentage(num) : getRoundPercentage(num)
            const isDone = percentage >= 100*/
            const isDone = false
            return (
              <div
                key={index}
                style={{
                  display: 'inline-block',
                  margin: '10px 5px 0 5px',
                  cursor: 'pointer',
                  opacity: pointsLogicId === activity.id ? 1 : 0.5,
                }}
                onClick={setPointsCustomType(activity.id)}
              >
                <YumActivityIcon
                  category={activity.icon || activity.category}
                  color={isDone ? 'darkGreen' : 'darkBlue'}
                  bg={'white'}
                />
              </div>
            )
          })}
      </div>
    </>
  )

  return (
    <div className={classes.wrapper}>
      <Box
        className={classes.mainWrapper}
        style={{
          // backgroundColor: dayPercentage === 100 /*&& !isBinary*/ ? lighterGreen : lightBlue,
          backgroundColor: dayPercentage === 100 ? challengeGoalGreen : challengeGoalGray,
        }}
      >
        {/*<div style={{ textAlign: 'center' }}>
          {customType} | {pointsLogicId?.substring(0, 8)} | {isBinary && 'isBinary'}
        </div>*/}
        <Box className={classes.mainHeader}>
          <YumButton
            className={classes.plainButton}
            onClick={handlePrevDay}
            disabled={!prevDate}
            style={{ opacity: !prevDate ? 0.5 : 1 }}
          >
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              {isRtl ? (
                <path d="M13 22L19 16L13 10" stroke="#24274B" strokeWidth="2" />
              ) : (
                <path d="M19 22L13 16L19 10" stroke="#24274B" strokeWidth="2" />
              )}
            </svg>
          </YumButton>
          <div className={classes.activeDateWrapper}>
            <div className={classes.activeDate}>
              <NativeSelect
                className={classes.plainSelect}
                value={activeDate}
                onChange={handleActiveDateChange}
                inputProps={{
                  className: classes.activeDateSelectInput,
                  name: 'dates',
                  style: {
                    width: activeDateWidth,
                  },
                }}
              >
                {getDates(startDate, endDate)
                  .reverse()
                  .map(({ text, value }, index) => (
                    <option key={index} value={value}>
                      {text}
                    </option>
                  ))}
              </NativeSelect>
            </div>
          </div>
          <YumButton
            className={classes.plainButton}
            onClick={handleNextDay}
            disabled={!nextDate}
            style={{ opacity: !nextDate ? 0.5 : 1 }}
          >
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              {!isRtl ? (
                <path d="M13 22L19 16L13 10" stroke="#24274B" strokeWidth="2" />
              ) : (
                <path d="M19 22L13 16L19 10" stroke="#24274B" strokeWidth="2" />
              )}
            </svg>
          </YumButton>
        </Box>
        <Box
          className={classes.activityName}
          style={{
            marginBottom: !isBinary ? '20px' : '10px',
          }}
        >
          {isActivityIndex ? activityIndexTypeSelect : isPoints ? pointsLogicIdSelect : activityName}
          {!isPoints && !isActivityIndex && goal.dailyMax && goal.dailyMax > 0 ? (
            <div
              style={{ opacity: 0.75, fontSize: 12 }}
            >{`${dailyMaxLabel}: ${formatThousands(goal.dailyMax)} ${getChallengeProgressUnits(goal)}`}</div>
          ) : null}
          {/*{isActivityIndex ? activityIndexTypeSelect : activityName}
          {isPoints && pointsLogicIdSelect}*/}
        </Box>
        {!isBinary && (
          <>
            <span className={classes.dayValue}>{formatThousands(dayValue, digits)}</span>
            {isPoints ? (
              <>
                {' '}
                <span className={classes.units}>{units}</span>
              </>
            ) : (
              <>
                <span className={classes.dayGoal}>
                  {isPersistenceCustom ? ' / ' + formatThousands(dailyPersistenceGoal || 0) : ''}
                </span>{' '}
                <span className={classes.units}>{units}</span>
                {isPersistenceCustom && (
                  <div className={classes.progressBar}>
                    <div
                      className={classes.progressBarInner}
                      style={{
                        /*background: dayPercentage === 100 ? '#067c00' : '#24274B',*/
                        width: `${dayPercentage}%`,
                      }}
                    />
                  </div>
                )}
              </>
            )}
          </>
        )}
      </Box>
      <Box className={classes.formWrapper}>{isBinary ? binaryForm : amountForm}</Box>
    </div>
  )
}
